// src/components/common/StatusBadge.tsx
//
// The single place a status is turned into a badge. EmailTable used to carry its
// own inline copy of this map, which is how the two vocabularies drifted apart.

import React from 'react';
import {
  AlertCircle,
  Ban,
  CheckCircle,
  Clock,
  RefreshCw,
  Send,
  XCircle,
} from 'lucide-react';
import {
  StatusTone,
  describeDeliveryStatus,
  describeSmtpStatus,
} from '../../types/status';

interface StatusBadgeProps {
  status: string;
  type: 'smtp' | 'delivery';
}

const TONE_CLASS: Record<StatusTone, string> = {
  neutral: 'bg-gray-100 text-gray-700',
  progress: 'bg-blue-100 text-blue-700',
  good: 'bg-green-100 text-green-700',
  warn: 'bg-amber-100 text-amber-800',
  bad: 'bg-red-100 text-red-700',
};

// Keyed on the stored value rather than the tone, so Sending can spin and
// Blocked can differ from a plain failure.
const ICONS: Record<string, React.ReactNode> = {
  Queued: <Clock size={14} />,
  Processing: <RefreshCw size={14} className="animate-spin" />,
  Sending: <RefreshCw size={14} className="animate-spin" />,
  Accepted: <CheckCircle size={14} />,
  Failed: <XCircle size={14} />,
  Blocked: <Ban size={14} />,
  Pending: <Clock size={14} />,
  InTransit: <Send size={14} />,
  Retrying: <RefreshCw size={14} />,
  TemporaryFailure: <AlertCircle size={14} />,
  PermanentFailure: <XCircle size={14} />,
  Expired: <Clock size={14} />,
  Bounced: <XCircle size={14} />,
  SoftBounce: <AlertCircle size={14} />,
  Unknown: <AlertCircle size={14} />,
};

const StatusBadge: React.FC<StatusBadgeProps> = ({ status, type }) => {
  const display =
    type === 'smtp' ? describeSmtpStatus(status) : describeDeliveryStatus(status);

  return (
    <span
      title={display.description}
      className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full ${TONE_CLASS[display.tone]}`}
    >
      {ICONS[status] ?? <AlertCircle size={14} />}
      {display.label}
    </span>
  );
};

export default StatusBadge;
